import { Particle } from "./Particle";
import type { IGrid } from "./types";

/**
 * 网格类
 * 管理所有粒子和渲染数据
 */
export class Grid implements IGrid {
    width: number;
    height: number;
    pixels: Particle[];
    // 用于渲染的缓冲区
    colorBuffer: Uint32Array;
    
    // 全局重力向量，默认为向下 (0, 1)
    // 范围建议在 -1 到 1 之间
    gravity: { x: number; y: number } = { x: 0, y: 1 };

    constructor(width: number, height: number) {
        this.width = width;
        this.height = height;
        this.pixels = new Array(width * height);
        this.colorBuffer = new Uint32Array(width * height);
        this.clear();
    }

    /**
     * 清空网格
     */
    clear() {
        for (let i = 0; i < this.pixels.length; i++) {
            // 直接创建类似 Empty 的粒子，打破循环依赖
            this.pixels[i] = new Particle({ color: 0x000000, empty: true, name: 'Empty' });
            this.colorBuffer[i] = 0x000000;
        }
    }

    /**
     * 调整网格大小
     * @param newWidth 新宽度
     * @param newHeight 新高度
     */
    resize(newWidth: number, newHeight: number) {
        if (newWidth === this.width && newHeight === this.height) return;

        const newPixels = new Array(newWidth * newHeight);
        const newColorBuffer = new Uint32Array(newWidth * newHeight);

        // 初始化新网格为 Empty 粒子
        for (let i = 0; i < newPixels.length; i++) {
            newPixels[i] = new Particle({ color: 0x000000, empty: true });
            newColorBuffer[i] = 0x000000;
        }

        // 复制旧数据
        const copyWidth = Math.min(this.width, newWidth);
        const copyHeight = Math.min(this.height, newHeight);

        for (let y = 0; y < copyHeight; y++) {
            const oldRowStart = y * this.width;
            const newRowStart = y * newWidth;
            for (let x = 0; x < copyWidth; x++) {
                newPixels[newRowStart + x] = this.pixels[oldRowStart + x];
                newColorBuffer[newRowStart + x] = this.colorBuffer[oldRowStart + x] ?? 0;
            }
        }

        this.width = newWidth;
        this.height = newHeight;
        this.pixels = newPixels;
        this.colorBuffer = newColorBuffer;
    }

    /**
     * 获取粒子
     * @param x 横坐标
     * @param y 纵坐标
     * @returns 粒子实例，如果越界则返回 null
     */
    get(x: number, y: number): Particle | null {
        if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
            return null;
        }
        const index = y * this.width + x;
        return this.pixels[index] || null;
    }

    /**
     * 设置粒子
     * @param x 横坐标
     * @param y 纵坐标
     * @param particle 粒子实例
     */
    set(x: number, y: number, particle: Particle) {
        if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
            return;
        }
        const index = y * this.width + x;
        this.setIndex(index, particle);
    }

    setIndex(index: number, particle: Particle) {
        if (index >= 0 && index < this.pixels.length) {
            this.pixels[index] = particle;
            // 颜色更新推迟到渲染阶段或立即更新
            // 这里为了简单，不立即更新 colorBuffer，而是由外部循环或专门的 updateBuffer 处理
            // 但为了保持一致性，也可以在这里更新
            // 注意：Pixi Texture 通常需要 ABGR 或 RGBA 格式，取决于平台 endianness
            // 我们假设使用 standard hex color，在同步到 buffer 时再处理
        }
    }

    /**
     * 交换两个位置的粒子
     */
    swap(a: number, b: number) {
        if (this.isValid(a) && this.isValid(b)) {
            const temp = this.pixels[a]!;
            this.pixels[a] = this.pixels[b]!;
            this.pixels[b] = temp;
        }
    }

    /**
     * 检查位置是否为空（即是否是 Empty 粒子）
     * 支持重载：(index) 或 (x, y)
     */
    isEmpty(indexOrX: number, y?: number): boolean {
        let index = indexOrX;
        if (typeof y === 'number') {
            // 如果提供了 x, y，先检查边界
            if (indexOrX < 0 || indexOrX >= this.width || y < 0 || y >= this.height) {
                return false;
            }
            index = y * this.width + indexOrX;
        }
        return index >= 0 && index < this.pixels.length && this.pixels[index]!.empty;
    }

    /**
     * 检查索引是否在网格内
     */
    isValid(index: number): boolean {
        return index >= 0 && index < this.pixels.length;
    }

    /**
     * 每一帧的更新逻辑
     */
    update() {
        // 重置所有粒子的 updated 标记
        for (let i = 0; i < this.pixels.length; i++) {
             if (this.pixels[i]) {
                this.pixels[i]!.updated = false;
             }
        }

        // 根据重力方向决定遍历顺序
        // 如果重力向下 (y > 0)，从下往上遍历 (height-1 -> 0)
        // 如果重力向上 (y < 0)，从上往下遍历 (0 -> height-1)
        // 如果重力向右 (x > 0)，从右往左遍历 (width-1 -> 0)
        // 如果重力向左 (x < 0)，从左往右遍历 (0 -> width-1)
        
        // 我们需要综合考虑 x 和 y。通常主要轴向决定了主要的遍历顺序。
        // 为了简单起见，如果 y 是主要重力方向，我们改变行遍历顺序。
        // 如果 x 是主要重力方向，我们改变列遍历顺序。
        
        // 实际上，只要遍历方向与运动方向相反即可避免“穿透”或“瞬间移动”问题。
        
        let startY = 0, endY = this.height, stepY = 1;
        if (this.gravity.y > 0) {
            startY = this.height - 1;
            endY = -1;
            stepY = -1;
        }

        let startX = 0, endX = this.width, stepX = 1;
        // 如果 x 重力显著，我们也需要反向遍历 x，否则粒子会像多米诺骨牌一样瞬间滑过整行
        // 但这里我们简单点，每一行内部随机左右遍历已经能解决大部分问题
        // 除非重力非常大导致 x 速度很快
        
        // 不过为了支持任意方向，最好是全动态
        if (this.gravity.x > 0) {
            startX = this.width - 1;
            endX = -1;
            stepX = -1;
        }

        // 注意：随机左右遍历 logic (leftToRight) 在这里会冲突
        // 如果我们要严格控制 x 遍历顺序，就不能用随机左右了
        // 或者只在 gravity.x 接近 0 时使用随机左右
        
        const useRandomX = Math.abs(this.gravity.x) < 0.2;

        for (let row = startY; row !== endY; row += stepY) {
            const rowOffset = row * this.width;
            
            // 决定这一行的遍历顺序
            // 如果 gravity.x 很大，则必须逆向遍历
            // 否则随机以模拟扩散
            
            let currentStartX = startX;
            let currentEndX = endX;
            let currentStepX = stepX;

            if (useRandomX) {
                 if (Math.random() > 0.5) {
                     currentStartX = 0;
                     currentEndX = this.width;
                     currentStepX = 1;
                 } else {
                     currentStartX = this.width - 1;
                     currentEndX = -1;
                     currentStepX = -1;
                 }
            }

            for (let i = currentStartX; i !== currentEndX; i += currentStepX) {
                const index = rowOffset + i;
                const particle = this.pixels[index];
                
                if (particle && !particle.empty) {
                    particle.update(this, index);
                }
            }
        }
    }
}
